import { useEffect, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { type WebOpportunity } from '../../../lib/opportunities';
import { pileOrder } from '../../../lib/opportunity-pile';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { Button, TextButton, Typography } from '../ui';
import { OpportunitiesModal } from './OpportunitiesModal';
import styles from './OpportunityFanDeck.module.css';

// The DESKTOP (wide-band) opportunities deck (Figma 12486:86630): a HORIZONTAL fan of agent-proposed
// outcomes (OPPORTUNITY goals), under a section header (Figma 13528:99219 — the title on the leading
// edge, an underlined "View more" trailing). The front card is white and fully open on the right
// (workspace eyebrow, title, and a "Talk to …" footer); the ≤2 behind cascade toward the leading edge —
// one fan step and one layer down each, and progressively greyer (neutral-100 then neutral-200) —
// showing only a ~30px head. Clicking a head raises that card to the front (native's deck reorder —
// tap only, NO swipe).
//
// "View more" opens the full list in `OpportunitiesModal` (Figma 12815-166600). It sits in the header
// and is shown ALWAYS — even below the pile size — so the section reads the same at any count.
// `OpportunityPile` switches between this and `OpportunityDeck` on the band's stack breakpoint.

const PAGE = 'home';

interface Props {
  opportunities: WebOpportunity[];
  /** Resolves the card's eyebrow — the owning workspace's name (the pile aggregates every workspace). */
  workspaceName: (opportunity: WebOpportunity) => string | undefined;
}

export function OpportunityFanDeck({ opportunities, workspaceName }: Props) {
  const { t } = useTranslation();
  const [frontId, setFrontId] = useState<string | null>(null);
  const { logPageClickBi } = useGenericBi();
  const ordered = pileOrder(opportunities, frontId);

  const [modalOpen, setModalOpen] = useState(false);
  const openModal = () => {
    logPageClickBi(
      { page_name: PAGE, button_name: 'opportunities_view_more' },
      { componentName: 'opportunities-view-more' },
    );
    setModalOpen(true);
  };

  // The header keeps the modal entrypoint always enabled, so — unlike the old canExpand gate — close it
  // explicitly if the list empties out, rather than relying only on this component's null-return unmount.
  useEffect(() => {
    if (opportunities.length === 0) setModalOpen(false);
  }, [opportunities.length]);

  if (ordered.length === 0) return null;

  // `--peeks` (0–2) is how many cards fan behind the front — it drives both the fan offset and the width
  // the deck reserves on the leading edge for their heads.
  return (
    <div className={styles.pile}>
      <header className={styles.header}>
        <Typography variant="h4" color="primary" component="h2" style={{ fontWeight: 'var(--sym-weight-medium)' }} className={styles.heading}>
          {t('home.opportunities.heading')}
        </Typography>
        <TextButton size="small" underline className={styles.viewToggle} onClick={openModal}>
          {t('home.opportunities.viewMoreLink')}
        </TextButton>
      </header>
      <div className={styles.deck} style={{ '--peeks': ordered.length - 1 } as React.CSSProperties}>
        {ordered.map((opportunity, i) => {
          const depth = ordered.length - 1 - i;
          return (
            <OpportunityCard
              key={opportunity.id}
              opportunity={opportunity}
              workspace={workspaceName(opportunity)}
              front={depth === 0}
              depth={depth}
              onBringForward={() => {
                setFrontId(opportunity.id);
                logPageClickBi({ page_name: PAGE, button_name: 'raise_opportunity_card' });
              }}
            />
          );
        })}
      </div>
      <OpportunitiesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        opportunities={opportunities}
        workspaceName={workspaceName}
      />
    </div>
  );
}

function OpportunityCard({
  opportunity,
  workspace,
  front,
  depth,
  onBringForward,
}: {
  opportunity: WebOpportunity;
  workspace?: string;
  front: boolean;
  depth: number;
  onBringForward: () => void;
}) {
  const { t } = useTranslation();
  const { short } = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const { openChatWithMessage } = useActiveConversation();

  const talk = () => {
    logPageClickBi({ page_name: PAGE, button_name: 'opportunity_talk' }, { componentName: 'opportunity-talk' });
    openChatWithMessage({
      kind: 'new',
      message: t('home.opportunities.chatPrompt', { title: opportunity.title }),
    });
  };

  return (
    <article className={styles.card} data-depth={depth} data-front={front || undefined}>
      <div className={styles.content}>
        <div className={styles.head}>
          {workspace && (
            <Typography variant="h4" color="strong" component="p" noWrap className={styles.eyebrow}>
              {workspace}
            </Typography>
          )}
          <Typography variant="h2" color="strong" component={front ? 'h3' : 'p'} className={styles.title}>
            {opportunity.title}
          </Typography>
        </div>
        <div className={styles.footer}>
          <Button onClick={talk}>{t('home.opportunities.cta', { name: short })}</Button>
        </div>
      </div>
      {/* A card behind: its whole exposed head is the affordance that brings it forward. The overlay sits
          above the card's own (unreachable) content, so a click raises rather than talks. */}
      {!front && (
        <button
          type="button"
          className={styles.raise}
          aria-label={t('home.opportunities.bringForward', { title: opportunity.title })}
          onClick={onBringForward}
        />
      )}
    </article>
  );
}
